import * as Actions from '../actions/activityCategoryActions';
import ActivityCategory from '../../entities/activityCategory';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type ActivityCategoryAction = { 
    type: string; 
    activityCategory: ActivityCategory | null; 
    activityCategoryId: string | ActivityCategory; 
    partial: boolean; 
    lastFetch: number; 
    activityCategories: ActivityCategory[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<ActivityCategory> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {ActivityCategory[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<ActivityCategory> | null, firmId: string, userId: string, updates?: ActivityCategory[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'activityCategory', 'activityCategories', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<ActivityCategory>} data
 * @param {ActivityCategory[]} [updates]
 * @returns {ArrayMap<ActivityCategory>}
 */
function updateStorage(data: ArrayMap<ActivityCategory>, updates?: ActivityCategory[]): ArrayMap<ActivityCategory> {
	if (logging) console.debug('⮂ activityCategoryReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {ActivityCategory[]} [updates]
 * @returns {ArrayMap<ActivityCategory>}
 */
function updateStorageNoReturn(updates?: ActivityCategory[]): void {
	if (logging) console.debug('⮂ activityCategoryReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {ActivityCategory[]} [data]
 * @returns {ArrayMap<ActivityCategory>}
 */
function createState(data?: ActivityCategory[] | ArrayMap<ActivityCategory>): ArrayMap<ActivityCategory> {
	if (!data) return ArrayMapRegistry.clone<ActivityCategory>('activityCategory', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<ActivityCategory>('activityCategory', data.map(e => e));
    else return ArrayMapRegistry.clone<ActivityCategory>('activityCategory', data);
}

function activityCategories(state: ArrayMap<ActivityCategory>, action: ActivityCategoryAction): ArrayMap<ActivityCategory> {
	if (!state) state = createState();

	let st: ArrayMap<ActivityCategory>;

	switch (action.type) {
		// action.activityCategory - the new value of activityCategory with id action.id
		// action.activityCategoryId - id of the target, used if new value is null
		case Actions.ACTIVITYCATEGORY_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ activityCategoryReducer: FETCH_ONE_SUCCESS', action);

			if (action.activityCategory === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.activityCategoryId)) {
				st = state.clone();
				st.set(action.activityCategory);
				// state.clear();

				updateStorageNoReturn([action.activityCategory]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.activityCategories
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.activityCategories
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.activityCategories
			 !partial, !stateEmpty, fetchEmpty	-> D return activityCategories in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge activityCategories in state newer than those in action.activityCategories
		 */
		case Actions.ACTIVITYCATEGORY_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.activityCategories.length === 0;
			const fetched = action.activityCategories.map(activityCategory => new ActivityCategory({...activityCategory, persisted: true}));

			if (logging) console.debug(`⮂ activityCategoryReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} activityCategories with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ activityCategoryReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ activityCategoryReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<ActivityCategory> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: ActivityCategory[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(activityCategory => {
					const current = st.get(activityCategory.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && activityCategory.status === 'D') {
							// Remove it from state
							toUpdate.push(activityCategory);
							st.delete(activityCategory);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (activityCategory.addedOn > current.addedOn) {
								toUpdate.push(activityCategory);
								st.set(activityCategory);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (activityCategory.status !== 'D') {
							toUpdate.push(activityCategory);
							st.set(activityCategory);
						}
						else if (logging) console.debug('⮂ activityCategoryReducer: partialMerge: skipping deleted activityCategory not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ activityCategoryReducer: empty: activityCategories', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ activityCategoryReducer: fullMerge: activityCategories', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localActivityCategory => {
                    // If this activityCategory has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localActivityCategory.persisted) {
                        const fetchedActivityCategory = st.get(localActivityCategory.id);
                        if (!fetchedActivityCategory || localActivityCategory.addedOn > fetchedActivityCategory.addedOn) st.set(localActivityCategory);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.ACTIVITYCATEGORY_FETCH_ONE_FAILURE:
			console.error('ACTIVITYCATEGORY_FETCH_ONE_FAILURE', action.activityCategoryId, action.err);

			// No update to storage required
			return state;

		case Actions.ACTIVITYCATEGORY_FETCH_FAILURE:
			console.error('ACTIVITYCATEGORY_FETCH_FAILURE', action.activityCategoryId, action.err);

			// No update to storage required
			return state;

		case Actions.ACTIVITYCATEGORY_RESTORE: {
		    let activityCategory = new ActivityCategory({...action.activityCategory});
            st = state.clone();

            const existing = st.get(activityCategory.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (activityCategory.addedOn >= existing.addedOn) {
                    st.set(activityCategory);
                    return updateStorage(st, [activityCategory]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (activityCategory.status === 'D') return state;

                st.set(activityCategory);
                return updateStorage(st, [activityCategory]);
            }
        }
            
		case Actions.ACTIVITYCATEGORY_SAVE: {
		    // console.debug('⮂ ACTIVITYCATEGORY_SAVE reducer', action.activityCategory?.id);
		    
			// clone the activityCategory
			let activityCategory = new ActivityCategory({...action.activityCategory});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new activityCategory in the state
			// If this is a create, add to the queue
			const _activityCategory = st.get(activityCategory.id);

			if (!_activityCategory) st.set(activityCategory);
			else {
				if (activityCategory.addedOn >= _activityCategory.addedOn) st.set(activityCategory);
			}

			return updateStorage(st, [activityCategory]);
		}

		case Actions.ACTIVITYCATEGORY_SAVE_MANY: {
		    if (logging) console.debug('⮂ ACTIVITYCATEGORY_SAVE_MANY reducer', action.activityCategories.map(activityCategory => activityCategory.id).join(','));
		    
			// clone the activityCategories
			let toProcess = action.activityCategories.map(activityCategory => new ActivityCategory(activityCategory));

			st = state.clone();
			// state.clear();

			const updates: ActivityCategory[] = [];

			toProcess.forEach(activityCategory => {
				// Add or update the new activityCategory in the state
				// If this is a create, add to the queue
				const existing = st.get(activityCategory.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (activityCategory.status !== 'D') {
						st.push(activityCategory);
						updates.push(activityCategory);
					}
					else if (logging) console.debug('⮂ ACTIVITYCATEGORY_SAVE_MANY: skipping deleted activityCategory not in state');
				}
				else {
					if (activityCategory.addedOn >= existing.addedOn) {
						st.set(activityCategory);
						updates.push(activityCategory);
					}
					else if (logging) console.debug('⮂ Skipping, existing activityCategory had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ACTIVITYCATEGORY_REMOVE: {
			// clone the activityCategory
			const activityCategory = new ActivityCategory({...action.activityCategory});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(activityCategory);

			return updateStorage(st, [activityCategory]);
		}

		case Actions.ACTIVITYCATEGORY_RESTORE_FAILURE:
		case Actions.ACTIVITYCATEGORY_SAVE_FAILURE: {
		    console.warn('ACTIVITYCATEGORY_SAVE_FAILURE reducer', action.activityCategory?.id);
		    if (!action.activityCategory?.id) {
				console.warn('ACTIVITYCATEGORY_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const activityCategory = ensure(ActivityCategory, action.activityCategory);
			if (!activityCategory) {
				console.error('Could not find activityCategory after call to save');
				return state;
			}

			st = state.clone();

			const _activityCategoryRef = state.get(activityCategory.id);

			if (_activityCategoryRef && (_activityCategoryRef.addedOn === activityCategory.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _activityCategory = new ActivityCategory({..._activityCategoryRef});
				_activityCategory.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_activityCategory.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('activityCategory', _activityCategory).then(r => {
						if (r.isErr()) console.error(`⮂ activityCategoryReducer: failed to save activityCategory with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ activityCategoryReducer: saved activityCategory with fatalError info');
					}).catch(err => console.error(`⮂ activityCategoryReducer: failed to save activityCategory with fatalError info: ${err.message}`));

				st.set(_activityCategory);
			}

			return updateStorage(st);
		}

		case Actions.ACTIVITYCATEGORY_SAVE_MANY_FAILURE: {
		    console.warn('ACTIVITYCATEGORY_SAVE_MANY_FAILURE reducer', action.activityCategories.map(activityCategory => activityCategory.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.ACTIVITYCATEGORY_RESTORE_SUCCESS:
		case Actions.ACTIVITYCATEGORY_SAVE_SUCCESS: {
			if (!action.activityCategory?.id) {
				console.warn('ACTIVITYCATEGORY_SAVE_SUCCESS reducer');
                return state;
			}

			const activityCategoryRef = state.get(action.activityCategory?.id);
			if (!activityCategoryRef) {
				console.warn('Could not find activityCategory after call to save');
                return state;
			}

			st = state.clone();

			if (action.activityCategory.addedOn >= activityCategoryRef.addedOn) {
				st.set(action.activityCategory);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const activityCategory = new ActivityCategory({...activityCategoryRef, persisted: true});
				st.set(activityCategory);
			}

			return updateStorage(st, [action.activityCategory]);
		}

		case Actions.ACTIVITYCATEGORY_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ ACTIVITYCATEGORY_SAVE_MANY_SUCCESS reducer', action.activityCategories.map(activityCategory => activityCategory.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const activityCategories: ActivityCategory[] = [];

			action.activityCategories.forEach(e => {
				const activityCategoryRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!activityCategoryRef) console.warn('Could not find activityCategory after call to save');
				else if (e.addedOn >= activityCategoryRef.addedOn) {
				    st.set(e);
				    activityCategories.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const activityCategory = new ActivityCategory({...activityCategoryRef, persisted: true});
					st.set(activityCategory);
					activityCategories.push(activityCategory);
				}
			});

			return updateStorage(st, activityCategories);
		}

		case Actions.ACTIVITYCATEGORY_REMOVE_FAILURE: {
		    console.warn('ACTIVITYCATEGORY_REMOVE_FAILURE action', action.activityCategory?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.ACTIVITYCATEGORY_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.ACTIVITYCATEGORY_RESTORE_MANY: {
			if (logging) console.debug('⮂ ACTIVITYCATEGORY_RESTORE_MANY reducer', action.activityCategories.map(activityCategory => activityCategory.id).join(','));

			const toProcess = action.activityCategories.map(activityCategory => new ActivityCategory({...activityCategory}));
			st = state.clone();

			const updates: ActivityCategory[] = [];

			toProcess.forEach(activityCategory => {
				const existing = st.get(activityCategory.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (activityCategory.addedOn >= existing.addedOn) {
						st.set(activityCategory);
						updates.push(activityCategory);
					}
					else if (logging) console.debug('⮂ ACTIVITYCATEGORY_RESTORE_MANY: skipping older version of activityCategory');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (activityCategory.status !== 'D') {
						st.set(activityCategory);
						updates.push(activityCategory);
					}
					else if (logging) console.debug('⮂ ACTIVITYCATEGORY_RESTORE_MANY: skipping deleted activityCategory not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ACTIVITYCATEGORY_REMOVE_MANY: {
			if (logging) console.debug('⮂ ACTIVITYCATEGORY_REMOVE_MANY reducer', action.activityCategories.map(activityCategory => activityCategory.id).join(','));

			const toProcess = action.activityCategories.map(activityCategory => new ActivityCategory({...activityCategory}));
			st = state.clone();

			toProcess.forEach(activityCategory => {
				st.delete(activityCategory);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default activityCategories;